import { makeAutoObservable, runInAction } from 'mobx'
import { isCancel } from 'axios'
import { logger } from 'shared/lib'
import { TableStore } from 'shared/ui/TableNew'
import { RequestCancelAxios } from 'shared/api/lib'
import { ContactsApi } from 'entities/Contacts'
import { type IParamsContacts } from 'entities/Contacts/api/types'
import { type IParamsFiltersList } from 'entities/Broadcast/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { RowContact } from 'entities/Contacts/model/Table/RowContact'
import { type Contact } from 'entities/Contacts/model/Contact'
import { type EnrollmentFilters } from 'widgets/ContactEnrollment'

export class EnrollmentContactsTableStore {
  private readonly _tableStore: TableStore<RowContact>
  private _itemsMap: Map<number | string, RowContact> = new Map()
  private _filters: EnrollmentFilters | null = null
  private _excludeFilters: EnrollmentFilters | null = null
  private _cancelRequest = new RequestCancelAxios()

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._tableStore = new TableStore({
      key: 'enrollment_table',
      page: 1,
    })
  }

  get tableStore() {
    return this._tableStore
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get filtersListGroups(): IParamsFiltersList {
    return {
      contacts: this._filters?.contacts.map((item) => item.id) || [],
      segments: this._filters?.segments.map((item) => item.id) || [],
      tags: this._filters?.tags.map((item) => item.id) || [],
      advanced: this._filters?.filters.map((item) => item.filtersParams) || [],
    }
  }

  get excludeFiltersListGroups(): IParamsFiltersList {
    return {
      contacts: this._excludeFilters?.contacts.map((item) => item.id) || [],
      segments: this._excludeFilters?.segments.map((item) => item.id) || [],
      tags: this._excludeFilters?.tags.map((item) => item.id) || [],
      advanced: this._excludeFilters?.filters.map((item) => item.filtersParams) || [],
    }
  }

  get expandedParams(): IParamsContacts {
    return {
      ...this._tableStore.params,
      filtersListGroups: this.filtersListGroups,
      excludeFiltersListGroups: this.excludeFiltersListGroups,
    }
  }

  onChangePagination = (page: number, length: number) => {
    this._tableStore.setPage(page)
    this._tableStore.setLimit(length)

    this.fetchContacts()
  }

  reset = () => {
    this._filters = null
    this._excludeFilters = null
    this._itemsMap.clear()
    this._tableStore.dispose()
    this._cancelRequest.reset()
  }

  fetchContacts = async () => {
    try {
      this._itemsMap.clear()
      this._cancelRequest.init()

      runInAction(() => {
        this._tableStore.setLoading(true)
      })

      let results: Array<Contact> = []
      let total = 0

      const { data } = await ContactsApi.getContactsList(this.expandedParams, {
        cancelToken: this._cancelRequest.token,
      })

      total = data.total || 0
      results = this._entities.contactsStore.addItems(data.data || [])

      runInAction(() => {
        results.forEach((item) => {
          this._itemsMap.set(item.id, new RowContact(item))
        })

        this._tableStore.setTotal(total)
        this._tableStore.setLoading(false)
      })
    } catch (e) {
      if (!isCancel(e)) {
        this._tableStore.setLoading(false)
      }

      logger.error(e)
    }
  }

  setFilter = (filters: EnrollmentFilters, excludeFilters: EnrollmentFilters) => {
    this._filters = filters
    this._excludeFilters = excludeFilters
  }

  setTotal = (value: number) => {
    this._tableStore.setTotal(value)
  }
}
